import { motion } from "framer-motion";
import { MapPin, Truck, Phone, MessageCircle, ChevronRight, Package, Info } from "lucide-react";
import { Layout } from "@/components/Layout";
import { MenuCard } from "@/components/MenuCard";
import { AddOnCard } from "@/components/AddOnCard";
import { 
  MENU_SETS, 
  ADD_ONS, 
  DELIVERY_AREAS, 
  CONTACT_INFO 
} from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Home() {
  return (
    <Layout>
      <main className="flex flex-col gap-16 md:gap-24 pb-20">
        {/* Hero Section */}
        <section id="hero" className="relative w-full h-[70vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={IMAGES.MANDY_DISH_1} 
              alt="Authentic Nasi Arab Mandy"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>
          
          <motion.div 
            className="container mx-auto px-4 z-10 text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-4 tracking-tighter"
            >
              NASI ARAB <span className="text-primary">MERWIY</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8"
            >
              Authentic Nasi Arab Mandy Ayam & Kambing delivered straight to your doorstep in Meru, Klang, and surrounding areas.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a 
                href="#menu"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-semibold rounded-full hover:scale-[1.02] active:scale-[0.97] transition-all duration-200 shadow-lg shadow-primary/20"
              >
                View Menu <ChevronRight className="w-4 h-4" />
              </a>
              <a 
                href={CONTACT_INFO.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-secondary text-secondary-foreground font-semibold rounded-full border border-border hover:bg-accent transition-all duration-200"
              >
                <MessageCircle className="w-4 h-4" /> Order Now
              </a>
            </motion.div>
          </motion.div>
        </section>

        {/* Delivery Info Banner */}
        <section className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[ 
              { icon: MapPin, title: "Based in Meru", desc: "Klang Valley Delivery Coverage" },
              { icon: Truck, title: "Grab & Lalamove", desc: "Reliable third-party delivery services" },
              { icon: Phone, title: "Fast Response", desc: "Direct WhatsApp ordering system" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 p-6 rounded-2xl bg-card border border-border">
                <div className="p-3 rounded-full bg-primary/10">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Menu Section */}
        <section id="menu" className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Signature Sets</h2>
            <p className="text-muted-foreground">Choose from our Ayam (Chicken) or Kambing (Mutton) Mandy platters.</p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {MENU_SETS.map((set) => (
              <motion.div key={set.id} variants={staggerItem}>
                <MenuCard menuSet={set} />
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Add-ons Section */}
        <section id="addons" className="container mx-auto px-4">
          <div className="bg-accent/30 rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <Package className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Extra Add-ons</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ADD_ONS.map((addOn) => (
                <AddOnCard key={addOn.id} addOn={addOn} />
              ))}
            </div>
          </div>
        </section>

        {/* Delivery & Coverage Section */}
        <section id="delivery" className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6">Delivery Coverage</h2>
                <p className="text-muted-foreground mb-8">
                  We provide fast and reliable delivery using Grab Express, Lalamove, or our personal riders to ensure your food arrives fresh and hot.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {DELIVERY_AREAS.map((area, index) => (
                  <div key={index} className="flex items-center gap-2 text-foreground font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    {area}
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-4 p-6 bg-secondary rounded-2xl border border-border">
                <Info className="w-6 h-6 text-primary shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Important Note</h4>
                  <p className="text-sm text-muted-foreground">
                    Delivery fees are calculated based on your distance from our base in Meru, Klang. We use real-time rates from delivery partners.
                  </p>
                </div>
              </div>
            </div>

            <motion.div 
              className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
            >
              <img 
                src="/images/delivery_service_20260218_084915.png" 
                alt="Food delivery service"
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = IMAGES.DELIVERY_1;
                }}
              />
              <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
            </motion.div>
          </div>
        </section>

        {/* Simple Contact Footer */}
        <section className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center gap-6 py-12 border-t border-border">
            <h2 className="text-2xl font-bold">Ready to Order?</h2>
            <p className="text-muted-foreground max-w-md">
              Contact us directly on WhatsApp to place your order or inquire about daily availability.
            </p>
            <a 
              href={CONTACT_INFO.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-10 py-5 bg-primary text-primary-foreground font-bold rounded-full text-lg shadow-xl shadow-primary/25 hover:scale-105 transition-transform"
            >
              <MessageCircle className="w-6 h-6" />
              Chat on WhatsApp
            </a>
            <p className="font-mono text-sm opacity-60 tracking-widest">
              {CONTACT_INFO.phone}
            </p>
          </div>
        </section>
      </main>
    </Layout>
  );
}
